import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Business,
  LocalShipping,
  Security,
  SupportAgent,
} from '@mui/icons-material';

const About = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Business />,
      title: t('about.features.quality.title'),
      description: t('about.features.quality.desc'),
    },
    {
      icon: <LocalShipping />,
      title: t('about.features.delivery.title'),
      description: t('about.features.delivery.desc'),
    },
    {
      icon: <Security />,
      title: t('about.features.secure.title'),
      description: t('about.features.secure.desc'),
    },
    {
      icon: <SupportAgent />,
      title: t('about.features.support.title'),
      description: t('about.features.support.desc'),
    },
  ];

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" gutterBottom fontWeight="bold">
            {t('about.hero.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" maxWidth="md" mx="auto">
            {t('about.hero.subtitle')}
          </Typography>
        </Box>

        {/* About Content */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {t('about.ourStory.title')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('about.ourStory.content1')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('about.ourStory.content2')}
              </Typography>
              <Typography variant="body1">
                {t('about.ourStory.content3')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {t('about.ourMission.title')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('about.ourMission.description')}
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                {t('about.ourMission.valuesTitle')}
              </Typography>
              <Typography variant="body1" component="div">
                <ul>
                  <li>{t('about.ourMission.values.quality')}</li>
                  <li>{t('about.ourMission.values.integrity')}</li>
                  <li>{t('about.ourMission.values.customer')}</li>
                  <li>{t('about.ourMission.values.innovation')}</li>
                </ul>
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Features Grid */}
        <Typography variant="h4" textAlign="center" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          {t('about.whyChooseUs.title')}
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Stats Section */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('about.stats.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                50K+
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('about.stats.customers')}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                1000+
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('common.products')}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                100+
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('common.brands')}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                24/7
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('common.support')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default About;
